import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { Link, Prompt, useParams } from "react-router-dom/cjs/react-router-dom";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Table from "react-bootstrap/Table";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";

import useFetch from "../../../hooks/fetchMSSQL-hook";
import { API_URL } from "../../../config/config";
import { NavigationContext } from "../../../context/navigation-context";
import { NotificationManager } from "react-notifications";
import TagsFilter from "../../../components/Modals/TagsFilter";
import sampleProduct from "../../../assets/images/sample-product.svg";

const normPath = (p) => (p ? String(p).replace(/\\/g, "/") : p);
const MAX_TEXT_LEN = 255;
const MAX_CODE_LEN = 100;
const MAX_DESC_LEN = 2000;

function ImagePicker({ apiUrl, valuePath, file, previewUrl, onPick, onClear, disabled }) {
  const inputRef = useRef(null);
  const src = previewUrl ? previewUrl : valuePath ? `${apiUrl}/${normPath(valuePath)}` : null;

  return (
    <div className="w-100">
      <input ref={inputRef} type="file" accept="image/png" style={{ display: "none" }} onChange={onPick} disabled={disabled} />
      <div className="border rounded d-flex align-items-center w-100" style={{ padding: 10, background: "#fff", boxShadow: "0 2px 10px rgba(0,0,0,.04)", opacity: disabled ? 0.75 : 1 }}>
        <div className="border rounded overflow-hidden d-flex align-items-center justify-content-center mr-3" style={{ width: 76, height: 76, background: "#f8f9fa", flex: "0 0 76px" }}>
          {src ? <img src={src} alt="Podgląd" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => (e.currentTarget.src = sampleProduct)} /> : <div className="text-muted text-center" style={{ fontSize: 12 }}>Brak</div>}
        </div>

        <div className="d-flex flex-column w-100" style={{ minWidth: 0 }}>
          <div className="d-flex align-items-center justify-content-between" style={{ gap: 10 }}>
            <div></div>
            <div className="d-flex" style={{ gap: 8, flex: "0 0 auto" }}>
              <button type="button" className="btn btn-sm btn-primary" disabled={disabled} onClick={() => inputRef.current?.click()}><i className="fas fa-upload mr-2" />Wybierz</button>
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={disabled || !src} onClick={() => { onClear(); if (inputRef.current) inputRef.current.value = ""; }}><i className="fas fa-times mr-2" />Wyczyść</button>
            </div>
          </div>
          <div className="text-muted mt-1" style={{ fontSize: 12 }}>Najlepiej kwadratowe PNG (np. 512x512).</div>
        </div>
      </div>
    </div>
  );
}

export default function Recipe() {
  const { id: recipeId } = useParams();
  const { fetchMssqlApi } = useFetch();
  const { setHeaderData } = useContext(NavigationContext);

  const [loading, setLoading] = useState(true);
  const [dirtySettings, setDirtySettings] = useState(false);
  const [dirtyIngredients, setDirtyIngredients] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [uiMsg, setUiMsg] = useState(null);

  const [vats, setVats] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [ingredientsOptions, setIngredientsOptions] = useState([]);
  const [ingredientsRows, setIngredientsRows] = useState([]);

  const [formState, setFormState] = useState({
    RecipeId: null, EAN: null, Name: "", VATCode: "", VATName: "", Description: "", Code: "",
    DepositId: null, DepositName: "", Image: "", IsActive: true
  });

  const [tags, setTags] = useState([]);
  const [initialTagIds, setInitialTagIds] = useState([]);
  const [tagsOpen, setTagsOpen] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  const revokePreview = (url) => { try { if (url) URL.revokeObjectURL(url); } catch (e) {} };

  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;
  const selectStyles = { menuPortal: (b) => ({ ...b, zIndex: 999999 }), menu: (b) => ({ ...b, zIndex: 999999 }) };

  const saveTagsAsync = () => {
    const current = (tags || []).flatMap((g) => g.options).filter((o) => o.isActive).map((o) => o.tagId);
    const added = current.filter((id) => !initialTagIds.includes(id)), removed = initialTagIds.filter((id) => !current.includes(id));
    const total = added.length + removed.length; if (!total) return Promise.resolve(true);

    return new Promise((resolve) => {
      let done = 0; const next = () => { done += 1; if (done >= total) { setInitialTagIds(current); resolve(true); } };
      added.forEach((tagId) => fetchMssqlApi("recipe-tag", { method: "POST", hideNotification: true, data: { recipeId, tagId } }, next, next));
      removed.forEach((tagId) => fetchMssqlApi("recipe-tag", { method: "DELETE", hideNotification: true, data: { recipeId, tagId } }, next, next));
    });
  };

  useEffect(() => {
    setHeaderData({ text: "Podgląd receptury" });
    setLoading(true); setDirtySettings(false); setDirtyIngredients(false); setSubmitAttempted(false); setUiMsg(null);

    let left = 5; const done = () => { left -= 1; if (left <= 0) setLoading(false); };

    fetchMssqlApi(`recipe/${recipeId}`, { hideNotification: true }, (res) => {
      const recipe = res?.[0];
      setFormState({
        RecipeId: recipe?.RecipeId, EAN: recipe?.EAN, Name: recipe?.Name || "",
        VATCode: recipe?.VatCode || "", VATName: recipe?.VATName || "",
        Description: recipe?.Description || "", Code: recipe?.Code || "",
        DepositId: recipe?.DepositId ?? null, DepositName: recipe?.DepositName || "", Image: recipe?.Image || "",
        IsActive: recipe?.IsActive ?? recipe?.Active ?? true
      });
      setLoading(false);
      done();
    }, () => {
      setLoading(false);
      done();
    });

    fetchMssqlApi("vats", { method: "POST", hideNotification: true, showLoader: false, data: { isActive: true } }, (res) => { setVats(res || []); done(); }, done);

    fetchMssqlApi("deposits", { method: "POST", hideNotification: true, showLoader: false, data: { isActive: null } }, (res) => { setDeposits(res || []); done(); }, done);

    fetchMssqlApi("products", { method: "POST", hideNotification: true, showLoader: false, data: { isIngredient: true } }, (res) => {
      const list = res || [];
      setIngredients(list);
      setIngredientsOptions(list.map((i) => ({ value: i.ProductId, label: i.Name })));
      done();
    }, done);

    fetchMssqlApi(`recipe-products/${recipeId}`, { hideNotification: true }, (res) => {
      const list = res || [];
      setIngredientsRows(list.map((RI) => ({
        RecipeProductId: RI?.RecipeProductId, RecipeId: RI?.RecipeId, ProductId: RI?.ProductId ?? null,
        Name: RI?.Name ?? "", Value: RI?.Value ?? "", _isNew: false, _deleted: false
      })));
      done();
    }, done);

    fetchMssqlApi("recipes-tags", { hideNotification: true }, (resTags) => {
      const all = resTags;
      fetchMssqlApi(`recipe-tags?recipeId=${recipeId}`, { hideNotification: true }, (resRecipeTags) => {
        const active = resRecipeTags;
        const activeIds = (active || []).map((rt) => rt.TagId);
        const grouped = (all || []).reduce((acc, t) => {
          const parts = String(t?.Name || "").split(" - "), category = parts[0] || "Inne", name = parts[1] || parts[0] || "Tag";
          if (!acc[category]) acc[category] = [];
          acc[category].push({ tagId: t.TagId, name, isActive: activeIds.includes(t.TagId), fullName: t?.Name});
          return acc;
        }, {});
        setTags(Object.keys(grouped).map((cat) => ({ label: cat, options: grouped[cat] })));
        setInitialTagIds(activeIds);
        done();
      }, done);
    }, done);
    // eslint-disable-next-line
  }, [recipeId]);

  const selectedTagBadges = (tags || []).flatMap((g) => g.options).filter((o) => o.isActive).map((o) => (
    <span key={o.tagId} className="btn btn-primary badge badge-pill px-2 py-2 font-weight-bold">{o.fullName}</span>
  ));

  const errors = useMemo(() => {
    const e = {}, name = String(formState.Name || ""), code = String(formState.Code || ""), desc = String(formState.Description || "");
    if (!name.trim()) e.Name = "Nazwa jest wymagana"; else if (name.length > MAX_TEXT_LEN) e.Name = `Max ${MAX_TEXT_LEN} znaków`;
    if (!code.trim()) e.Code = "Kod dodatkowy jest wymagany"; else if (code.length > MAX_CODE_LEN) e.Code = `Max ${MAX_CODE_LEN} znaków`;
    if (!formState.VATCode) e.VATCode = "Wybierz VAT";
    if (desc && desc.length > MAX_DESC_LEN) e.Description = `Max ${MAX_DESC_LEN} znaków`;
    return e;
  }, [formState]);

  const rowErrors = useMemo(() => {
    const map = {};
    (ingredientsRows || []).forEach((r) => {
      if (r?._deleted) return;
      const isBlankNew = !!r?._isNew && !r?.ProductId && !String(r?.Name || "").trim() && !String(r?.Value || "").trim();
      if (isBlankNew) return;

      const re = {};
      if (!r?.ProductId) re.Name = "Wybierz składnik";
      const v = String(r?.Value ?? "").trim(), n = v === "" ? null : Number(String(v).replace(",", "."));
      if (v === "") re.Value = "Podaj ilość"; else if (!Number.isFinite(n) || n <= 0) re.Value = "Niepoprawna ilość";
      if (Object.keys(re).length) map[r.RecipeProductId] = re;
    });
    return map;
  }, [ingredientsRows]);

  const showErrors = (dirtySettings || dirtyIngredients || submitAttempted) && !loading;
  const canSaveSettings = useMemo(() => Object.keys(errors).length === 0, [errors]);
  const canSaveIngredients = useMemo(() => Object.keys(rowErrors).length === 0, [rowErrors]);

  const setField = (name) => (e) => { if (loading) return; const value = e?.target?.value ?? ""; setDirtySettings(true); setUiMsg(null); setFormState((p) => ({ ...p, [name]: value})); };

  const toggleActive = () => { if (loading) return; setDirtySettings(true); setUiMsg(null); setFormState((p) => ({ ...p, IsActive: !p.IsActive })); };

  const clearImage = () => { if (loading) return; setDirtySettings(true); setUiMsg(null); revokePreview(imagePreviewUrl); setImageFile(null); setImagePreviewUrl(null); setFormState((p) => ({ ...p, Image: "" })); };

  const onPickImage = (e) => {
    if (loading) return;
    const input = e.target, file = input?.files?.[0] || null;
    revokePreview(imagePreviewUrl);
    if (!file) return;
    if (file.type !== "image/png") return NotificationManager.error("Tylko PNG", "Błąd", 2500);
    setDirtySettings(true); setUiMsg(null); setImageFile(file); setImagePreviewUrl(URL.createObjectURL(file)); if (input) input.value = "";
  };

  const addIngredientRow = () => { if (loading) return; setDirtyIngredients(true); setUiMsg(null); setIngredientsRows((p) => (p || []).concat({ RecipeProductId: -Date.now(), RecipeId: Number(recipeId), ProductId: null, Name: "", Value: "", _isNew: true, _deleted: false })); };

  const setRowProduct = (rowId) => (opt) => { if (loading) return; setDirtyIngredients(true); setUiMsg(null); setIngredientsRows((p) => (p || []).map((r) => r.RecipeProductId === rowId ? { ...r, ProductId: opt?.value ?? null, Name: opt?.label ?? "" } : r)); };

  const setRowValue = (rowId) => (e) => {
    if (loading) return;
    setDirtyIngredients(true); setUiMsg(null);
    let raw = String(e?.target?.value ?? "").replace(",", ".").replace(/[^0-9.]/g, "");
    const parts = raw.split("."); if (parts.length > 2) raw = parts[0] + "." + parts.slice(1).join("");
    setIngredientsRows((p) => (p || []).map((r) => r.RecipeProductId === rowId ? { ...r, Value: raw } : r));
  };

  const deleteIngredientRow = (row) => {
    if (loading) return;
    setDirtyIngredients(true); setUiMsg(null);
    if (row?._isNew) return setIngredientsRows((p) => (p || []).filter((r) => r.RecipeProductId !== row.RecipeProductId));
    setIngredientsRows((p) => (p || []).map((r) => r.RecipeProductId === row.RecipeProductId ? { ...r, _deleted: true } : r));
  };

  const restoreIngredientRow = (row) => { if (loading) return; setDirtyIngredients(true); setUiMsg(null); setIngredientsRows((p) => (p || []).map((r) => r.RecipeProductId === row.RecipeProductId ? { ...r, _deleted: false } : r)); };

  const saveIngredientsAsync = () => {
    if (Object.keys(rowErrors).length) return NotificationManager.error("Popraw składniki (puste pola / ilość)", "Błąd", 2500), Promise.resolve(false);
    return new Promise((resolve) => {
      const activeRows = (ingredientsRows || []).slice(); let pending = 0, aborted = false;
      const finish = (ok) => { if (aborted) return; aborted = true; resolve(ok); };
      const done = () => { pending -= 1; if (pending <= 0) { setIngredientsRows((p) => (p || []).filter((r) => !r._deleted)); finish(true); } };
      const addDone = (created, tmpRecipeProductId) => {setIngredientsRows((p) => (p || []).map((x) => x?.RecipeProductId === tmpRecipeProductId ? { ...x, RecipeProductId: created.RecipeProductId ?? x.RecipeProductId, _isNew: false } : x));
      done();
    }
      for (const r of activeRows) {
        const isBlankNew = !!r?._isNew && !r?.ProductId && !String(r?.Name || "").trim() && !String(r?.Value || "").trim();
        if (isBlankNew) continue;
        if (r?._deleted) {
          if (!r._isNew) {
            pending += 1;
            fetchMssqlApi(`recipe-product`, { method: "DELETE", hideNotification: true, data: {recipeProductId: r.RecipeProductId} }, done, () => { NotificationManager.error("Nie udało się usunąć składnika", "Błąd", 2000); done(); });
          }
          continue;
        }
        const prod = (ingredients || []).find((p) => Number(p.ProductId) === Number(r.ProductId)) || (ingredients || []).find((p) => String(p.Name) === String(r.Name));
        if (!prod?.EAN) return NotificationManager.error(`Nie znaleziono składnika: "${r.Name || "-"}"`, "Błąd", 2500), finish(false);
        const payload = { recipeProductId: Number(r.RecipeProductId) ,recipeId: Number(recipeId), productId: prod.ProductId, value: r.Value };
        pending += 1;

        r._isNew
          ? fetchMssqlApi("recipe-product", { method: "POST", hideNotification: true, data: payload }, (res) => addDone(res.rows[0], r.RecipeProductId), () => { NotificationManager.error("Nie udało się dodać składnika", "Błąd", 2000); done(); })
          : fetchMssqlApi("recipe-product", { method: "PUT", hideNotification: true, data: payload }, done, () => { NotificationManager.error(`Nie udało się zapisać składnika ${r.Name}`, "Błąd", 2000); done(); });
      }

      if (pending === 0) finish(true);
    });
  };

  const handleSubmitSettings = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true); setUiMsg(null);
    if (!canSaveSettings) return setUiMsg({ type: "err", text: "Popraw pola w ustawieniach" }), NotificationManager.error("Popraw pola w ustawieniach", "Błąd", 2500);

    const payload = {
      RecipeId: formState.RecipeId,
      Name: String(formState.Name || "").trim(),
      VATCode: String(formState.VATCode || ""),
      Description: String(formState.Description || ""),
      Code: String(formState.Code || "").trim(),
      IsActive: !!formState.IsActive,
      DepositId: formState.DepositId !== "" && formState.DepositId !== null && formState.DepositId !== undefined ? formState.DepositId : null,
      Image: formState.Image ? String(formState.Image) : ""
    };

    let dataToSend = payload;

    if (imageFile) {
      const fd = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (key === "Image" || value === null || value === undefined) return;
        fd.append(key, String(value));
      });
      fd.append("Image", imageFile);
      dataToSend = fd;
    }

    fetchMssqlApi(`recipe`, { method: "PUT", hideNotification: true, data: dataToSend},
      async () => { await saveTagsAsync(); NotificationManager.success("Zapisano ustawienia", "Sukces", 2000); setDirtySettings(false); },
      (err) => {
        const msg = err?.response?.data?.message || err?.data?.message || err?.message || "Nie udało się zapisać ustawień";
        setUiMsg({ type: "err", text: msg }); NotificationManager.error(msg, "Błąd", 3000);
      }
    );
  };

  const handleSubmitIngredients = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true); setUiMsg(null);
    if (!canSaveIngredients) return NotificationManager.error("Popraw składniki (puste pola / ilość)", "Błąd", 2500);

    const ok = await saveIngredientsAsync();
    if (!ok) return;

    NotificationManager.success("Zapisano składniki", "Sukces", 2000);
    setDirtyIngredients(false);
  };

  const nameErr = showErrors ? errors?.Name : null;
  const vatErr = showErrors ? errors?.VATCode : null;
  const codeErr = showErrors ? errors?.Code : null;
  const descErr = showErrors ? errors?.Description : null;

  const imgSrc = imagePreviewUrl ? imagePreviewUrl : formState.Image ? `${API_URL}/${normPath(formState.Image)}` : sampleProduct;

  return (
    <Container fluid className="py-2">
      <Prompt when={!loading && (dirtySettings || dirtyIngredients)} message={"Istnieją niezapisane zmiany, czy na pewno chcesz kontynuować?"} />

      <div className="mb-1">
        <Link to="/recipes" className="text-decoration-none">
          <Button variant="link" disabled={loading} className="p-0"><i className="fas fa-arrow-left mr-2" />Wróć</Button>
        </Link>
      </div>

      <Row>
        <Col md={12} xl={4} className="px-1">
          <Card className="shadow-sm">
            <Card.Header className="bg-white"><strong>Ustawienia</strong></Card.Header>
            <Form autoComplete="off" onSubmit={handleSubmitSettings}>
              <Card.Body>
                {uiMsg && <Alert variant={uiMsg.type === "ok" ? "success" : "danger"}>{uiMsg.text}</Alert>}

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">EAN</Form.Label>
                  <Col sm={8}><Form.Control size="sm" value={formState.EAN || ""} disabled /></Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">Nazwa</Form.Label>
                  <Col sm={8}>
                    <Form.Control size="sm" value={formState.Name} onChange={setField("Name")} maxLength={MAX_TEXT_LEN} disabled={loading} isInvalid={!!nameErr} />
                    <Form.Control.Feedback type="invalid">{nameErr}</Form.Control.Feedback>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">Stawka VAT</Form.Label>
                  <Col sm={8}>
                    <Form.Control as="select" size="sm" value={formState.VATCode || ""} onChange={setField("VATCode")} disabled={loading} isInvalid={!!vatErr}>
                      <option value="">{loading ? " " : "Wybierz"}</option>
                      {(vats || []).map((v) => <option key={v.Code} value={v.Code}>{v.Name}</option>)}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">{vatErr}</Form.Control.Feedback>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">Kaucja</Form.Label>
                  <Col sm={8}>
                    <Form.Control as="select" size="sm" value={formState.DepositId || ""} onChange={setField("DepositId")} disabled={loading}>
                      <option value="">{loading ? " " : "Brak"}</option>
                      {(deposits || []).filter((d) => d.IsActive || d.DepositId === formState.DepositId).map((d) => <option key={d.DepositId} value={d.DepositId}>{d.Name} ({d.Price} zł)</option>)}
                    </Form.Control>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">Kod dodatkowy</Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      size="sm"
                      type="text"
                      value={String(formState.Code ?? "")}
                      onInput={(e) => {
                        if (loading) return;
                        const value = e.currentTarget.value;
                        setDirtySettings(true);
                        setUiMsg(null);
                        setFormState((p) => ({ ...p, Code: value }));
                      }}
                      maxLength={MAX_CODE_LEN}
                      disabled={loading}
                      isInvalid={!!codeErr}
                    />
                    <Form.Control.Feedback type="invalid">{codeErr}</Form.Control.Feedback>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">Opis</Form.Label>
                  <Col sm={8}>
                    <Form.Control as="textarea" rows={4} size="sm" value={formState.Description ?? ""} onChange={setField("Description")} disabled={loading} isInvalid={!!descErr} />
                    <Form.Control.Feedback type="invalid">{descErr}</Form.Control.Feedback>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">Tagi</Form.Label>
                  <Col sm={8}>
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                      {selectedTagBadges}
                      <button type="button" className="btn btn-outline-primary badge badge-pill px-3 p-2" onClick={() => {setTagsOpen(true); setDirtySettings(true); }}>
                        <i className="fas fa-plus"/>
                      </button>
                    </div>
                  </Col>
                </Form.Group>

                <Form.Group as={Row} className="mb-2">
                  <Form.Label column sm={4} className="text-muted text-sm-right">Zdjęcie</Form.Label>
                  <Col sm={8}>
                    <ImagePicker apiUrl={API_URL} valuePath={formState.Image} file={imageFile} previewUrl={imagePreviewUrl} onPick={onPickImage} onClear={clearImage} disabled={loading} />
                  </Col>
                </Form.Group>
              </Card.Body>

              <Card.Footer className="bg-white d-flex justify-content-between" style={{ gap: 10 }}>
                <Button size="sm" variant={formState.IsActive ? "outline-danger" : "outline-success"} disabled={loading} onClick={toggleActive}>
                  {formState.IsActive ? "Dezaktywuj" : "Aktywuj"}
                </Button>

                <Button size="sm" type="submit" disabled={loading || !canSaveSettings || !dirtySettings}>
                  Zapisz ustawienia
                </Button>
              </Card.Footer>
            </Form>
          </Card>
        </Col>

        <Col md={12} xl={4} className="px-1">
          <Card className="shadow-sm">
            <Card.Header className="bg-white d-flex align-items-center justify-content-between">
              <div><strong>Składniki</strong> <Badge variant="primary">{(ingredientsRows || []).filter((r) => !r._deleted).length}</Badge></div>
              <div className="d-flex flex-column align-items-center">
                <i className="btn btn-outline-primary fas fa-plus" onClick={addIngredientRow}/>
              </div>
            </Card.Header>

            <Form onSubmit={handleSubmitIngredients}>
              <Card.Body className="p-0">
                <Table striped responsive className="mb-0">
                  <thead><tr><th>Składnik</th><th style={{ width: 160 }}>Ilość</th><th style={{ width: 40 }} /></tr></thead>
                  <tbody>
                    {(ingredientsRows || []).map((r) => {
                      const re = showErrors ? rowErrors[r.RecipeProductId] : null;
                      if (r._deleted) {
                        return (
                          <tr key={r.RecipeProductId} className="table-danger">
                            <td colSpan={2} className="text-muted">{r.Name || "(usunięty składnik)"}</td>
                            <td className="text-center"><Button size="sm" variant="link" onClick={() => restoreIngredientRow(r)}><i className="fas fa-trash-restore" /></Button></td>
                          </tr>
                        );
                      }

                      const selectedOpt = ingredientsOptions.find((o) => Number(o.value) === Number(r.ProductId)) || (r.Name ? { value: r.ProductId, label: r.Name } : null);

                      return (
                        <tr key={r.RecipeProductId}>
                          <td>
                            <Select
                              options={ingredientsOptions}
                              value={selectedOpt}
                              onChange={setRowProduct(r.RecipeProductId)}
                              isSearchable
                              placeholder="Wybierz składnik..."
                              menuPortalTarget={menuPortalTarget}
                              menuPosition="fixed"
                              menuPlacement="auto"
                              styles={selectStyles}
                              maxMenuHeight={420}
                            />
                            {re?.Name && <div className="text-danger mt-1" style={{ fontSize: 12 }}>{re.Name}</div>}
                          </td>

                          <td>
                            <Form.Control
                              size="sm"
                              value={r.Value}
                              onChange={setRowValue(r.RecipeProductId)}
                              isInvalid={!!re?.Value}
                              onBeforeInput={(e) => { if (!/^[0-9.,]$/.test(e.data ?? "")) e.preventDefault(); }}
                            />
                            {re?.Value && <div className="text-danger mt-1" style={{ fontSize: 12 }}>{re.Value}</div>}
                          </td>

                          <td className="text-center"><Button size="sm" variant="link" onClick={() => deleteIngredientRow(r)}><i className="fas fa-trash" /></Button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>

              <Card.Footer className="bg-white d-flex justify-content-end">
                <Button size="sm" type="submit" disabled={loading || !canSaveIngredients || !dirtyIngredients}>
                  Zapisz składniki
                </Button>
              </Card.Footer>
            </Form>
          </Card>

          {tagsOpen && (
            <TagsFilter
              tags={tags}
              setTags={(t) => { setTags(t); setDirtySettings(true); }}
              multitag={true}
              handleClose={() => { setTagsOpen(false); setDirtySettings(true); }}
            />
          )}
        </Col>

        <Col md={12} xl={4} className="px-1">
          <Card className="shadow-sm">
            <Card.Header className="bg-white"><strong>Podgląd zdjęcia</strong></Card.Header>
            <Card.Body>
              <img alt="#" src={imgSrc} onError={(e) => (e.currentTarget.src = sampleProduct)} style={{ width: "100%", height: "auto", objectFit: "cover", borderRadius: 10, border: "1px solid rgba(0,0,0,.08)", boxShadow: "0 8px 24px rgba(0,0,0,.08)", background: "#f8f9fa" }} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}